// The API base URL comes from the environment, never from source.
// Set REACT_APP_API_BASE_URL in .env (committed default) or .env.local (personal
// override). See .env.example.
//
// Create React App inlines REACT_APP_* at BUILD time, so a built bundle is tied
// to the API it was built against and cannot be repointed without rebuilding.
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

if (!apiBaseUrl) {
    // Fail loudly and immediately. A build that silently talks to the wrong API
    // is far worse than one that refuses to start.
    throw new Error(
        "REACT_APP_API_BASE_URL is not set. Copy .env.example to .env.local and " +
        "set it, or check that .env exists at the project root."
    );
}

export const BaseURL = apiBaseUrl;
export const LoginURL = "/login";

// Password recovery. These three were hardcoded inline in UserApiService with
// PascalCase paths — /RecoveryVerifyEmail, /RecoverVerifyOTP (note the
// inconsistent spelling between the two) and /RecoveryResetPassword —
// inherited from the Node backend of the template this app was built from.
// They do not follow this API's conventions and are NOT known to exist on the
// Laravel backend at all. Raised as BE-15. The screens are wired and will work
// the moment the endpoints land; correct the paths here when the backend
// confirms them, because this is now the only place they appear.
export const RecoveryVerifyEmailURL = "/RecoveryVerifyEmail";
export const RecoveryVerifyOTPURL = "/RecoverVerifyOTP";
export const RecoveryResetPasswordURL = "/RecoveryResetPassword";

export const UserListURL = "/users";
export const UserCreateURL = "/users";
export const UserShowURL = "/users";
export const UserUpdateURL = "/users";
export const UserDeleteURL = "/users";
export const UserLoginHistoryListURL = "/users/login/history";
export const UserPackageAssignURL = "/assign/package/user";

export const UserRoleListURL = "/roles";
export const UserRoleCreateURL = "/roles";
export const UserRoleUpdateURL = "/roles";
export const PermissionListURL = "/permissions";
export const RolePermissionShowURL = "/roles";
export const RolePermissionDeleteURL = "/roles";

export const BankListURL = "/bank/account";
export const BankCreateURL = "/bank/account";
export const BankUpdateURL = "/bank/account";
export const BankShowURL = "/bank/account";
export const BankDeleteURL = "/bank/account";
export const BankTypeListRL = "/get/bank/account/type";
export const CashBalanceURL = "/cash/balance";

export const SupplierListURL = "/suppliers";
export const SupplierCreateURL = "/suppliers";
export const SupplierUpdateURL = "/suppliers";
export const SupplierShowURL = "/suppliers";
export const SupplierDeleteURL = "/suppliers";
export const SupplierTypeListRL = "/supplier/type";

export const WarehouseListURL = "/warehouse";
export const WarehouseCreateURL = "/warehouse";
export const WarehouseUpdateURL = "/warehouse";
export const WarehouseShowURL = "/warehouse";
export const WarehouseDeleteURL = "/warehouse";
export const WarehouseTypeListRL = "/get/warehouse/type";

export const MeasurementListURL = "/measurement";

export const CategoryListURL = "/categories";
export const CategoryCreateURL = "/categories";
export const CategoryUpdateURL = "/categories";
export const CategoryShowURL = "/categories";
export const CategoryDeleteURL = "/categories";
export const MeasurementTypeListRL = "/measurement/type";

export const SubCategoryListURL = "/subcategories";
export const SubCategoryCreateURL = "/subcategories";
export const SubCategoryUpdateURL = "/subcategories";
export const SubCategoryShowURL = "/subcategories";
export const SubCategoryDeleteURL = "/subcategories";

export const ProductListURL = "/products";
export const ProductCreateURL = "/products";
export const ProductUpdateURL = "/products";
export const ProductShowURL = "/products";
export const ProductDeleteURL = "/products";
export const ProductGradeListURL = "/get/product/grade";
export const ProductListBySubCategory = "/get/products/subcategory";


export const PurchaseNoSeries = "/purchase/no/series";
export const PurchaseOrder = "/purchase/order";
export const PurchasePendingOrder = "/pending/purchase/order";
export const PurchaseRecieveOrder = "/recieve/purchase/order";

export const SalesNoSeries = "/sales/no/series";
export const CustomerNoSeries = "/customer/no/series";


export const CustomerTypeList = "/get/customer/type";
export const Customer = "/customer";
export const CustomerByCode = "/info/customer";

export const SalesOrder = "/sales/order"
export const SalesOrderList = "/sales/order"
// BE-05. The controller method existed and was fully written, but no route was
// ever registered for it — the whole path down to the repository was
// unreachable. It is routed now and paginated from day one.
//
// ⚠️ Expect it to be EMPTY in normal operation: POST /sales/order writes orders
// straight to `approved`, so nothing reaches `pending` through the counter. A
// zero count here is correct, not a fault.
export const SalesPendingOrder = "/pending/sales/order"
export const DueSalesOrderListByCustomerId = "/due/sales/order"
export const SalesOrderReturn = "/return/sales/order"

// ── Sales requests (what erp-mobile sends from the field) ───────────────────
//
// ⚠️ CALLED "SALES REQUESTS" IN THIS APP, NOT "DRAFTS". `src/drafts/` already
// means browser-local autosave here — the thing that keeps a half-typed invoice
// when a tab is closed. Two meanings of "draft" in one codebase is how somebody
// eventually wires up the wrong one, so the API path keeps the server's word
// and everything user-facing, every route and every module path says "request".
//
// These are NOT sales orders. A request moves no stock and creates no customer
// debt until an admin converts it — which is the whole reason it is a separate
// resource. See the backend's sales_drafts migration for the argument.
export const SalesRequestList = "/sales/drafts"
export const SalesRequest = "/sales/drafts"

export const TransactionList = "/transaction"
export const Transaction = "/transaction"
export const CreditTransaction = "/credit/transaction"

export const Dashboard = "/dashboard"

export const CreditCategory = "/credit/categories"
export const DocumentByCreditCategory = "/document/by/credit/category"
export const CreditTransactionList = "/credit/transaction"

export const Discount = "/discounts"
export const ProductDiscount = "/product/discounts"

export const SalesTarget = "/employeeSalesTargets"

// ---------------------------------------------------------------------------
// Endpoints the backend built in answer to BACKEND_BACKLOG.md, confirmed in
// erp-backend/API_REFERENCE.md (28 Jul 2026).
//
// Everything below this line was unavailable when the screens above were
// written. Several of them replace a guess, a workaround or a disabled feature
// flag elsewhere in the app — the comment on each says which.
// ---------------------------------------------------------------------------

// §2 — re-hydrates the user AND their flattened permission list. The login
// response carries the same object, but only login writes it; a page refresh
// nine hours later has nothing newer. Used to pick up permission changes an
// admin made mid-shift without forcing everyone to log out.
export const Me = "/me"

// §3 — multiple shops (BE-02, BE-02a, BE-02b, BE-03, BE-16).
// ShopList is the entitlement list: exactly the shops this user may act in.
// Shop is GET (with linked warehouses) and PUT (the invoice header) by id.
export const ShopList = "/shops"
export const Shop = "/shop"
// Every ACTIVE shop, for the user form to assign from. A different endpoint
// from ShopList on purpose: that one is the caller's own entitlement list, and
// an owner assigning somebody to a branch they do not work in themselves would
// never see it there. Requires `user.store`.
export const AssignableShops = "/shops/assignable"

// §12 — stock on hand (BE-01, BE-01a, BE-08).
// StockByProduct takes a product id and returns per-warehouse and per-lot
// breakdowns; StockLow is a bare array of everything under the 10-box
// threshold. Both require the `stock.index` permission, which is new.
export const StockByProduct = "/stock"
export const StockLow = "/stock/low"

// §10 — return-as-exchange (BE-14). One document, both legs, priced entirely
// server-side; the frontend sends no monetary field at all.
export const Exchange = "/exchange"

// §11 — money (B-3). The payment picker is built from PaymentMethods rather
// than a hardcoded list, because a cheque is neither cash nor bank: it does not
// settle instantly, so it must not move a bank balance until somebody clears
// it. PaymentPending is that queue; Payment + /clear or /bounce are the two
// ways out of it.
export const PaymentMethods = "/payment/methods"
export const PaymentPending = "/payment/pending"
export const Payment = "/payment"

// §11 — receivables ageing (BE-17). Optional ?customer_id= and ?as_of=.
export const CustomerAgeing = "/customer/ageing"

// BE-20 — the comparison endpoint (COMPARISON_RULES.md BR-31).
//
// ONE endpoint for every axis. Shop to shop, product to product, month to
// month and size to size are the same request with a different `dimension`,
// which is what stops four screens drifting into four definitions of "sales".
export const Comparison = "/comparison"
export const ComparisonOptions = "/comparison/options"

// §13 — the logged-in salesperson's own target (BE-19). Deliberately ungated
// server-side: it only ever returns the caller's own rows, so there is no
// permission to check and nothing to leak.
export const MySalesTarget = "/my/sales/target"

// Everyone's target progress, for whoever runs the shop. Gated on
// `employeeTarget.overview` — a DIFFERENT right from the five that govern the
// target catalogue, because this one reads what named people earned towards
// their bonus rather than which templates exist.
export const SalesTargetOverview = "/sales/targets/overview"

// One target with the documents behind its figure. Authorised by ownership
// first — your own target is always yours to audit — then by the overview
// right for anybody else's.
export const SalesTargetDetail = (id) => `/sales/targets/${id}/detail`